import { Navigate } from "react-router-dom"

import { useGetSastGuidanceById } from "@/api/useSettings"
import { App, Button } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { KAI_GUIDANCE_TAB_KEY } from "@/const/tab.constants"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { CheckCircleOutlined } from "@/icons"
import { Form, RESOURCES } from "@/organisms/Form"
import {
  useGetOptionsSastGuidance,
  useWithTenantConfigurations
} from "@/pages/SettingsPage/application/guidance/useGuidance"
import { GuidanceFormSection } from "@/pages/SettingsPage/components/organisms/GuidanceFormSection"
import { ResourcesGuidanceFormSection } from "@/pages/SettingsPage/components/organisms/ResourcesGuidanceFormSection"
import { TestImpactFormSection } from "@/pages/SettingsPage/components/organisms/TestImpactFormSection"
import { FIELD_NAMES, getInitialValues } from "./helpers"

import type { GuidanceFormProps } from "."

export const GuidanceForm = (props: GuidanceFormProps) => {
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "settings.kai-guidance.form"
  })
  const { entityId, custom, form } = Form.useForm()
  const [{ response, isLoading }, { sastLlmDisabled }] =
    useWithTenantConfigurations(useGetSastGuidanceById(entityId))
  const { isLoading: isOptionsLoading } = useGetOptionsSastGuidance()
  const { data } = response || {}

  const { notification } = App.useApp()
  const { logger, EVENTS } = useLogger()

  const navigateOnFinished = `/${NAMES_ROUTES.SETTINGS.ROOT}/${KAI_GUIDANCE_TAB_KEY}`

  if (!isLoading && sastLlmDisabled) return <Navigate to={navigateOnFinished} />

  const { actions, impactCtx } = props
  const { createCtx, updateCtx } = actions

  return (
    <Form
      loading={isLoading || isOptionsLoading}
      initialValues={getInitialValues(data)}
      navigateOnFinished={navigateOnFinished}
      onFinish={async (values, context) => {
        const { isNewEntity, message } = context
        const action = isNewEntity ? "created" : "updated"

        try {
          const { sessionId, name_details } = values

          const payload = {
            sessionId,
            name: name_details,
            enabled: true
          }

          if (isNewEntity) await createCtx.mutateAsync(payload)
          else if (!isNewEntity && entityId) {
            if (payload.sessionId === undefined) delete payload.sessionId

            await updateCtx.mutateAsync({
              needle: `/${entityId}`,
              data: payload
            })
          }

          const key = `guidance-saved-${entityId}`

          logger.info(EVENTS.ANALYTIC_EVENTS.GUIDANCE.SAVE, {
            action,
            category: "sast",
            inputLength: values.guidanceText?.length,
            canonicalLength: values.canonicalText?.length
          })
          notification.success({
            key,
            closable: true,
            duration: 0,
            placement: "bottomRight",
            icon: (
              <CheckCircleOutlined
                style={{ color: "var(--forest-green-6-color)" }}
              />
            ),
            message: tForm("notifications.success.message"),
            description: tForm("notifications.success.description"),
            actions: (
              <Button
                type="primary"
                size="small"
                onClick={() => notification.destroy(key)}
              >
                {tForm("notifications.success.gotIt")}
              </Button>
            )
          })
        } catch (error) {
          message.onFailed(values.name_details, { action, error })

          return Promise.reject()
        }
      }}
      onFieldsChange={(values, allValues) => {
        const names = values?.[0].name
        const name = names[0]
        const sessionId = allValues?.find(
          (item) => item.name?.[0] === "sessionId"
        )?.value
        const sessionIdPresent = !!sessionId || sessionId === null

        const isSpecificResourceChanged = names.includes(RESOURCES.SPECIFIC)

        if (isSpecificResourceChanged && names[names.length - 1] === "type")
          form.setFieldValue([...names.slice(0, -1), "value"], undefined)

        if (name === "name_details" && sessionIdPresent) custom.setIsDirty(true)
        else if (
          name === FIELD_NAMES.cweIds ||
          name === FIELD_NAMES.detectionRulesIds ||
          name === FIELD_NAMES.guidanceText ||
          name.includes("resources") ||
          isSpecificResourceChanged
        ) {
          impactCtx.reset()
          form.resetFields([FIELD_NAMES.guidanceTestImpact])

          if (
            name === FIELD_NAMES.guidanceText &&
            form.getFieldValue(FIELD_NAMES.canonicalText)
          )
            form.setFieldValue(FIELD_NAMES.canonicalText, "")

          if (sessionIdPresent) form.resetFields([FIELD_NAMES.sessionId])

          custom.setIsDirty(false)
        }
      }}
    >
      <Form.Section.Details
        noDescription
        label={tForm("guidanceDetails.name.label")}
      />
      <ResourcesGuidanceFormSection impactCtx={impactCtx} />
      <GuidanceFormSection impactCtx={impactCtx} />
      <TestImpactFormSection impactCtx={impactCtx} />
    </Form>
  )
}
